import React from 'react';
import { Check, ImageOff } from 'lucide-react';
import type { FormField, PictureChoiceConfig, OptionLayout, ThemeConfig } from '../../../types/forms';

interface PictureChoiceFieldProps {
  field: FormField;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  error?: string;
  disabled?: boolean;
  themeConfig?: ThemeConfig;
}

export const PictureChoiceField: React.FC<PictureChoiceFieldProps> = ({
  field,
  value,
  onChange,
  error,
  disabled,
  themeConfig,
}) => {
  const config = field.config as PictureChoiceConfig | undefined;
  const images = config?.images || [];
  const multiSelect = config?.multiSelect ?? false;

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

  const handleSelect = (label: string) => {
    if (disabled) return;

    if (multiSelect) {
      const newValue = selectedValues.includes(label)
        ? selectedValues.filter(v => v !== label)
        : [...selectedValues, label];
      onChange?.(newValue);
    } else {
      onChange?.(label);
    }
  };

  if (images.length === 0) {
    return (
      <div className="text-center py-8 text-neutral-500">
        <ImageOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Nenhuma imagem configurada</p>
      </div>
    );
  }

  const optionLayout = (field.config?.optionLayout as OptionLayout) || 'grid-2';
  const pictureLayoutClasses: Record<OptionLayout, string> = {
    vertical: 'flex flex-col gap-3',
    horizontal: 'flex flex-wrap gap-3',
    'grid-2': 'grid grid-cols-1 md:grid-cols-2 gap-3',
    'grid-3': 'grid grid-cols-1 md:grid-cols-3 gap-3',
  };

  return (
    <div className={pictureLayoutClasses[optionLayout] || 'grid grid-cols-2 gap-3'} role={multiSelect ? 'group' : 'radiogroup'} aria-label={field.label}>
      {images.map((image, index) => {
        const isSelected = selectedValues.includes(image.label);
        return (
          <button
            key={index}
            type="button"
            role={multiSelect ? 'checkbox' : 'radio'}
            aria-checked={isSelected}
            aria-label={image.label || 'Imagem'}
            onClick={() => handleSelect(image.label)}
            disabled={disabled}
            className={`
              relative rounded-xl overflow-hidden border-2 transition-all duration-200
              ${isSelected
                ? 'border-primary-500 ring-2 ring-primary-500/20'
                : `${error ? 'border-red-500' : 'border-neutral-700'} hover:border-neutral-500`
              }
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            <div className="aspect-video bg-neutral-800">
              <img
                src={image.url}
                alt={image.label}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            <div className="p-2 bg-neutral-900/80 text-sm text-center break-words" style={{ color: themeConfig?.textColor || undefined }}>
              {image.label}
            </div>
            {isSelected && (
              <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};
